import React, { useEffect, useState } from 'react';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import ArchiveRoundedIcon from '@mui/icons-material/ArchiveRounded';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import MarkEmailReadRoundedIcon from '@mui/icons-material/MarkEmailReadRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import {
  fetchAdminUsers,
  fetchContactMessages,
  fetchEmailLogs,
  fetchMailCenterOverview,
  fetchNotifications,
  markNotificationRead,
  updateContactMessage,
} from '../../api/mailCenter';

const contactStatuses = [
  { value: '', label: 'Tous les statuts' },
  { value: 'NEW', label: 'Nouveau' },
  { value: 'IN_PROGRESS', label: 'En cours' },
  { value: 'ANSWERED', label: 'Répondu' },
  { value: 'ARCHIVED', label: 'Archivé' },
];

const contactPriorities = [
  { value: '', label: 'Toutes les priorités' },
  { value: 'LOW', label: 'Basse' },
  { value: 'NORMAL', label: 'Normale' },
  { value: 'HIGH', label: 'Haute' },
  { value: 'URGENT', label: 'Urgente' },
];

const emailStatuses = [
  { value: '', label: 'Tous les statuts' },
  { value: 'PENDING', label: 'En attente' },
  { value: 'SENT', label: 'Envoyé' },
  { value: 'FAILED', label: 'Échec' },
];

const notificationSeverities = [
  { value: '', label: 'Toutes les sévérités' },
  { value: 'INFO', label: 'Info' },
  { value: 'WARNING', label: 'Warning' },
  { value: 'ERROR', label: 'Erreur' },
  { value: 'CRITICAL', label: 'Critique' },
];

const readStatuses = [
  { value: '', label: 'Tous les états' },
  { value: 'false', label: 'Non lu' },
  { value: 'true', label: 'Lu' },
];

function formatDateTime(value) {
  if (!value) {
    return 'n/a';
  }
  return new Date(value).toLocaleString('fr-FR');
}

function contactStatusLabel(status) {
  return contactStatuses.find((entry) => entry.value === status)?.label || status || 'n/a';
}

function priorityLabel(priority) {
  return contactPriorities.find((entry) => entry.value === priority)?.label || priority || 'n/a';
}

function emailStatusLabel(status) {
  return emailStatuses.find((entry) => entry.value === status)?.label || status || 'n/a';
}

function severityLabel(severity) {
  return notificationSeverities.find((entry) => entry.value === severity)?.label || severity || 'n/a';
}

function statusChipColor(status) {
  switch (status) {
    case 'NEW':
      return { bg: 'rgba(243,198,92,0.18)', color: '#8b6410' };
    case 'IN_PROGRESS':
      return { bg: 'rgba(125,208,211,0.18)', color: '#11636d' };
    case 'ANSWERED':
    case 'SENT':
      return { bg: 'rgba(80,170,110,0.18)', color: '#21643b' };
    case 'ARCHIVED':
      return { bg: 'rgba(106,122,132,0.18)', color: '#475860' };
    case 'FAILED':
    case 'ERROR':
      return { bg: 'rgba(228,104,82,0.18)', color: '#9a3f2d' };
    case 'CRITICAL':
      return { bg: 'rgba(181,46,46,0.18)', color: '#8a2020' };
    case 'WARNING':
      return { bg: 'rgba(243,198,92,0.18)', color: '#8b6410' };
    default:
      return { bg: 'rgba(106,122,132,0.14)', color: '#475860' };
  }
}

const MailCenterAdminPage = () => {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [overview, setOverview] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactDraft, setContactDraft] = useState({ status: 'NEW', priority: 'NORMAL', assignedToId: '' });
  const [contactFilters, setContactFilters] = useState({ status: '', priority: '', date: '' });
  const [emailFilters, setEmailFilters] = useState({ status: '', recipient: '', date: '' });
  const [notificationFilters, setNotificationFilters] = useState({ severity: '', isRead: '', date: '' });

  const loadOverview = async () => {
    const payload = await fetchMailCenterOverview();
    setOverview(payload);
  };

  const loadAdmins = async () => {
    const payload = await fetchAdminUsers();
    const nextAdmins = Array.isArray(payload)
      ? payload.filter((user) => String(user.role || '').toUpperCase() === 'ADMIN')
      : [];
    setAdmins(nextAdmins);
  };

  const loadContacts = async (filters = contactFilters) => {
    const payload = await fetchContactMessages(filters);
    setContacts(Array.isArray(payload) ? payload : []);
  };

  const loadEmailLogs = async (filters = emailFilters) => {
    const payload = await fetchEmailLogs(filters);
    setEmailLogs(Array.isArray(payload) ? payload : []);
  };

  const loadNotifications = async (filters = notificationFilters) => {
    const payload = await fetchNotifications(filters);
    setNotifications(Array.isArray(payload) ? payload : []);
  };

  const loadCurrentTab = async (currentTab = tab) => {
    if (currentTab === 0) {
      await loadContacts();
      return;
    }
    if (currentTab === 1) {
      await loadEmailLogs();
      return;
    }
    await loadNotifications();
  };

  const loadPage = async (showSpinner = true) => {
    if (showSpinner) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }

    setError('');
    try {
      await Promise.all([loadOverview(), loadAdmins(), loadCurrentTab(tab)]);
    } catch (loadError) {
      setError(loadError.message || 'Impossible de charger le Mail Center.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadPage(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = async (_event, nextTab) => {
    setTab(nextTab);
    setNotice('');
    setError('');
    setLoading(true);
    try {
      await loadCurrentTab(nextTab);
    } catch (loadError) {
      setError(loadError.message || 'Impossible de charger cet onglet.');
    } finally {
      setLoading(false);
    }
  };

  const openContactDialog = (contact) => {
    setSelectedContact(contact);
    setContactDraft({
      status: contact.status || 'NEW',
      priority: contact.priority || 'NORMAL',
      assignedToId: contact.assignedToId || '',
    });
  };

  const closeContactDialog = () => {
    setSelectedContact(null);
    setContactDraft({ status: 'NEW', priority: 'NORMAL', assignedToId: '' });
  };

  const handleSaveContact = async () => {
    if (!selectedContact) {
      return;
    }
    setSaving(true);
    setError('');
    setNotice('');
    try {
      await updateContactMessage(selectedContact.id, {
        status: contactDraft.status,
        priority: contactDraft.priority,
        assignedToId: contactDraft.assignedToId || null,
      });
      await Promise.all([loadOverview(), loadContacts()]);
      setNotice('Message de contact mis à jour.');
      closeContactDialog();
    } catch (saveError) {
      setError(saveError.message || 'Impossible de mettre à jour le message.');
    } finally {
      setSaving(false);
    }
  };

  const handleArchiveContact = async (contact) => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      await updateContactMessage(contact.id, { status: 'ARCHIVED' });
      await Promise.all([loadOverview(), loadContacts()]);
      setNotice('Message archivé.');
    } catch (archiveError) {
      setError(archiveError.message || 'Impossible d’archiver le message.');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkRead = async (notificationId) => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      await markNotificationRead(notificationId);
      await Promise.all([loadOverview(), loadNotifications()]);
      setNotice('Notification marquée comme lue.');
    } catch (markError) {
      setError(markError.message || 'Impossible de marquer la notification comme lue.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 4, background: 'linear-gradient(180deg, #f4efe6 0%, #eef3f6 100%)', minHeight: '100%' }}>
      <Stack spacing={3}>
        <Paper sx={{ p: 3.5, borderRadius: 5, background: 'linear-gradient(135deg, #17323c 0%, #355b63 100%)', color: '#f7f3ea' }}>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} justifyContent="space-between" alignItems={{ xs: 'stretch', lg: 'center' }}>
            <Box sx={{ maxWidth: 760 }}>
              <Chip label="Mail Center" sx={{ bgcolor: 'rgba(247,243,234,0.14)', color: '#f7f3ea', fontWeight: 700, mb: 1.5 }} />
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 1.25 }}>
                Centre de gestion des contacts, emails et notifications
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(247,243,234,0.82)', lineHeight: 1.7 }}>
                Les tickets issus du formulaire de contact, l’historique des emails sortants et les notifications administratives sont regroupés ici pour limiter le bruit et accélérer le traitement.
              </Typography>
            </Box>

            <Button
              variant="contained"
              onClick={() => loadPage(false)}
              disabled={loading || refreshing}
              startIcon={refreshing ? <CircularProgress size={18} color="inherit" /> : <RefreshRoundedIcon />}
              sx={{ bgcolor: '#f3c65c', color: '#17323c', fontWeight: 800, '&:hover': { bgcolor: '#e7b94f' } }}
            >
              Actualiser
            </Button>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} sx={{ mt: 2.5 }} flexWrap="wrap" useFlexGap>
            <Chip label={`Contacts NEW: ${overview?.contacts?.newCount ?? 0}`} sx={{ bgcolor: 'rgba(247,243,234,0.14)', color: '#f7f3ea' }} />
            <Chip label={`Contacts en cours: ${overview?.contacts?.inProgressCount ?? 0}`} sx={{ bgcolor: 'rgba(247,243,234,0.14)', color: '#f7f3ea' }} />
            <Chip label={`Notifications non lues: ${overview?.unreadNotifications ?? 0}`} sx={{ bgcolor: 'rgba(247,243,234,0.14)', color: '#f7f3ea' }} />
          </Stack>
        </Paper>

        {error ? <Alert severity="error">{error}</Alert> : null}
        {notice ? <Alert severity="success">{notice}</Alert> : null}

        <Paper sx={{ borderRadius: 5, overflow: 'hidden' }}>
          <Tabs value={tab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto" sx={{ px: 2, pt: 1, borderBottom: '1px solid rgba(23,50,60,0.08)' }}>
            <Tab label="Contacts" />
            <Tab label="Historique emails" />
            <Tab label="Notifications" />
          </Tabs>

          <Box sx={{ p: 3 }}>
            {loading ? (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <CircularProgress sx={{ color: '#17323c', mb: 1.5 }} />
                <Typography variant="body2" sx={{ color: '#4f646f' }}>
                  Chargement de l’onglet actif...
                </Typography>
              </Box>
            ) : null}

            {!loading && tab === 0 ? (
              <Stack spacing={2.2}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                  <TextField
                    select
                    label="Statut"
                    value={contactFilters.status}
                    onChange={(event) => setContactFilters({ ...contactFilters, status: event.target.value })}
                    sx={{ minWidth: 220 }}
                  >
                    {contactStatuses.map((option) => (
                      <MenuItem key={option.value || 'all'} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label="Priorité"
                    value={contactFilters.priority}
                    onChange={(event) => setContactFilters({ ...contactFilters, priority: event.target.value })}
                    sx={{ minWidth: 220 }}
                  >
                    {contactPriorities.map((option) => (
                      <MenuItem key={option.value || 'all'} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Date"
                    type="date"
                    value={contactFilters.date}
                    onChange={(event) => setContactFilters({ ...contactFilters, date: event.target.value })}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 200 }}
                  />
                  <Button variant="outlined" onClick={() => loadContacts()} sx={{ fontWeight: 800 }}>
                    Filtrer
                  </Button>
                </Stack>

                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Sujet</TableCell>
                        <TableCell>Priorité</TableCell>
                        <TableCell>Statut</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {contacts.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6}>
                            <Typography variant="body2" sx={{ color: '#6b7c84', py: 2 }}>
                              Aucun message de contact ne correspond aux filtres actuels.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : contacts.map((contact) => {
                        const statusStyle = statusChipColor(contact.status);
                        const priorityStyle = statusChipColor(contact.priority === 'URGENT' ? 'CRITICAL' : contact.priority === 'HIGH' ? 'WARNING' : contact.priority);
                        return (
                          <TableRow key={contact.id} hover>
                            <TableCell>{formatDateTime(contact.createdAt)}</TableCell>
                            <TableCell>{contact.email}</TableCell>
                            <TableCell>{contact.subject}</TableCell>
                            <TableCell>
                              <Chip size="small" label={priorityLabel(contact.priority)} sx={{ bgcolor: priorityStyle.bg, color: priorityStyle.color, fontWeight: 700 }} />
                            </TableCell>
                            <TableCell>
                              <Chip size="small" label={contactStatusLabel(contact.status)} sx={{ bgcolor: statusStyle.bg, color: statusStyle.color, fontWeight: 700 }} />
                            </TableCell>
                            <TableCell align="right">
                              <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                                <Button size="small" variant="outlined" startIcon={<OpenInNewRoundedIcon />} onClick={() => openContactDialog(contact)}>
                                  Ouvrir
                                </Button>
                                <Button size="small" variant="outlined" startIcon={<PersonAddAlt1RoundedIcon />} onClick={() => openContactDialog(contact)}>
                                  Assigner
                                </Button>
                                <Button size="small" variant="outlined" color="warning" startIcon={<ArchiveRoundedIcon />} onClick={() => handleArchiveContact(contact)} disabled={saving}>
                                  Archiver
                                </Button>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Stack>
            ) : null}

            {!loading && tab === 1 ? (
              <Stack spacing={2.2}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                  <TextField
                    select
                    label="Statut"
                    value={emailFilters.status}
                    onChange={(event) => setEmailFilters({ ...emailFilters, status: event.target.value })}
                    sx={{ minWidth: 220 }}
                  >
                    {emailStatuses.map((option) => (
                      <MenuItem key={option.value || 'all'} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Destinataire"
                    value={emailFilters.recipient}
                    onChange={(event) => setEmailFilters({ ...emailFilters, recipient: event.target.value })}
                    sx={{ minWidth: 260 }}
                  />
                  <TextField
                    label="Date"
                    type="date"
                    value={emailFilters.date}
                    onChange={(event) => setEmailFilters({ ...emailFilters, date: event.target.value })}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 200 }}
                  />
                  <Button variant="outlined" onClick={() => loadEmailLogs()} sx={{ fontWeight: 800 }}>
                    Filtrer
                  </Button>
                </Stack>

                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Destinataire</TableCell>
                        <TableCell>Template</TableCell>
                        <TableCell>Statut</TableCell>
                        <TableCell>Provider</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {emailLogs.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5}>
                            <Typography variant="body2" sx={{ color: '#6b7c84', py: 2 }}>
                              Aucun email sortant n’a encore été journalisé.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : emailLogs.map((emailLog) => {
                        const statusStyle = statusChipColor(emailLog.status);
                        return (
                          <TableRow key={emailLog.id} hover>
                            <TableCell>{formatDateTime(emailLog.sentAt || emailLog.createdAt)}</TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#17323c' }}>
                                {emailLog.recipient}
                              </Typography>
                              {emailLog.errorMessage ? (
                                <Typography variant="caption" sx={{ color: '#9a3f2d' }}>
                                  {emailLog.errorMessage}
                                </Typography>
                              ) : null}
                            </TableCell>
                            <TableCell>{emailLog.templateCode || 'n/a'}</TableCell>
                            <TableCell>
                              <Chip size="small" label={emailStatusLabel(emailLog.status)} sx={{ bgcolor: statusStyle.bg, color: statusStyle.color, fontWeight: 700 }} />
                            </TableCell>
                            <TableCell>{emailLog.provider}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Stack>
            ) : null}

            {!loading && tab === 2 ? (
              <Stack spacing={2.2}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                  <TextField
                    select
                    label="Sévérité"
                    value={notificationFilters.severity}
                    onChange={(event) => setNotificationFilters({ ...notificationFilters, severity: event.target.value })}
                    sx={{ minWidth: 220 }}
                  >
                    {notificationSeverities.map((option) => (
                      <MenuItem key={option.value || 'all'} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label="État"
                    value={notificationFilters.isRead}
                    onChange={(event) => setNotificationFilters({ ...notificationFilters, isRead: event.target.value })}
                    sx={{ minWidth: 220 }}
                  >
                    {readStatuses.map((option) => (
                      <MenuItem key={option.value || 'all'} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Date"
                    type="date"
                    value={notificationFilters.date}
                    onChange={(event) => setNotificationFilters({ ...notificationFilters, date: event.target.value })}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 200 }}
                  />
                  <Button variant="outlined" onClick={() => loadNotifications()} sx={{ fontWeight: 800 }}>
                    Filtrer
                  </Button>
                </Stack>

                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Sévérité</TableCell>
                        <TableCell>Titre</TableCell>
                        <TableCell>Statut</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {notifications.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5}>
                            <Typography variant="body2" sx={{ color: '#6b7c84', py: 2 }}>
                              Aucune notification administrative disponible.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : notifications.map((notification) => {
                        const severityStyle = statusChipColor(notification.severity);
                        return (
                          <TableRow key={notification.id} hover>
                            <TableCell>{formatDateTime(notification.lastOccurredAt || notification.createdAt)}</TableCell>
                            <TableCell>
                              <Chip size="small" label={severityLabel(notification.severity)} sx={{ bgcolor: severityStyle.bg, color: severityStyle.color, fontWeight: 700 }} />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#17323c' }}>
                                {notification.title}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#6b7c84' }}>
                                {notification.occurrenceCount || 1} occurrence{notification.occurrenceCount > 1 ? 's' : ''}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip size="small" label={notification.read ? 'Lu' : 'Non lu'} sx={{ bgcolor: notification.read ? 'rgba(106,122,132,0.14)' : 'rgba(243,198,92,0.18)', fontWeight: 700 }} />
                            </TableCell>
                            <TableCell align="right">
                              {!notification.read ? (
                                <Button size="small" variant="outlined" startIcon={<MarkEmailReadRoundedIcon />} onClick={() => handleMarkRead(notification.id)} disabled={saving}>
                                  Marquer lu
                                </Button>
                              ) : null}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Stack>
            ) : null}
          </Box>
        </Paper>
      </Stack>

      <Dialog open={Boolean(selectedContact)} onClose={closeContactDialog} maxWidth="md" fullWidth>
        <DialogTitle>Détail du message de contact</DialogTitle>
        <DialogContent dividers>
          {selectedContact ? (
            <Stack spacing={2.2} sx={{ pt: 1 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField label="Email" value={selectedContact.email || ''} InputProps={{ readOnly: true }} fullWidth />
                <TextField label="Créé le" value={formatDateTime(selectedContact.createdAt)} InputProps={{ readOnly: true }} fullWidth />
              </Stack>
              <TextField label="Sujet" value={selectedContact.subject || ''} InputProps={{ readOnly: true }} fullWidth />
              <TextField label="Message" value={selectedContact.message || ''} InputProps={{ readOnly: true }} multiline minRows={6} fullWidth />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  select
                  label="Statut"
                  value={contactDraft.status}
                  onChange={(event) => setContactDraft({ ...contactDraft, status: event.target.value })}
                  fullWidth
                >
                  {contactStatuses.filter((option) => option.value).map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Priorité"
                  value={contactDraft.priority}
                  onChange={(event) => setContactDraft({ ...contactDraft, priority: event.target.value })}
                  fullWidth
                >
                  {contactPriorities.filter((option) => option.value).map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
              </Stack>
              <TextField
                select
                label="Assigné à"
                value={contactDraft.assignedToId}
                onChange={(event) => setContactDraft({ ...contactDraft, assignedToId: event.target.value })}
                fullWidth
              >
                <MenuItem value="">Non assigné</MenuItem>
                {admins.map((admin) => (
                  <MenuItem key={admin.id} value={admin.id}>{admin.login}</MenuItem>
                ))}
              </TextField>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeContactDialog}>Fermer</Button>
          <Button onClick={handleSaveContact} variant="contained" disabled={saving}>
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MailCenterAdminPage;